import { ReactNode, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { hasAnyPermissionForJob } from '../../constants/permissions';
import SubTabs from '../common/sub-tabs';

export interface SubTabbedItem {
  key: string;
  label: string;
  icon?: string;
  /** Governing jobs, OR'd. Empty = always visible. Mirrors GroupScreen's rule. */
  jobs: string[];
  element: ReactNode;
}

interface SubTabbedPanelProps {
  items: SubTabbedItem[];
  /** Query parameter holding the inner choice. `?tab=` is the outer strip's. */
  param?: string;
  testId?: string;
}

/**
 * Two lists that belong to one idea, shown under one tab.
 *
 * Notification Types and Notification Master are one job split in half — a
 * master cannot exist without a type, and the type owns the channels and
 * delivery policy the master inherits. Recipients and Recipient Lists are the
 * same story: a list is a group of recipients, and lists nest lists. Eight
 * top-level tabs wrapped onto two rows and made the strip a paragraph to read
 * rather than a row to scan.
 *
 * The lists stay SEPARATE — this only groups how they are reached. Each keeps
 * its own table, filters, paging and create button.
 *
 * The choice lives in its own query parameter so a sub-tab is linkable and
 * survives a refresh, exactly as the outer `?tab=` does. Unknown or forbidden
 * values fall back to the first visible item rather than rendering nothing, so a
 * stale bookmark degrades quietly.
 */
const SubTabbedPanel = ({ items, param = 'view', testId }: SubTabbedPanelProps) => {
  const permissionKeys = useSelector(
    (s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []
  );

  const visible = useMemo(() => {
    const keys = new Set<string>(permissionKeys);
    return items.filter((i) => i.jobs.length === 0 || i.jobs.some((j) => hasAnyPermissionForJob(j, keys)));
  }, [items, permissionKeys]);

  const [params, setParams] = useSearchParams();
  const wanted = visible.findIndex((i) => i.key === params.get(param));
  const active = visible[wanted >= 0 ? wanted : 0];

  if (!visible.length) return null;

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {/* One item left after permission filtering is not a choice, so the
          control is not drawn — the tab simply shows what the person may see. */}
      {visible.length > 1 && (
        <SubTabs
          tabs={visible.map((i) => ({ key: i.key, label: i.label, icon: i.icon }))}
          active={active.key}
          testId={testId ? `${testId}.SubTabs` : undefined}
          onChange={(key) => {
            const next = new URLSearchParams(params);
            next.set(param, key);
            // Replaced, not pushed: the back button should leave the screen
            // rather than walk the inner strip, matching GroupScreen.
            setParams(next, { replace: true });
          }}
        />
      )}
      {active.element}
    </div>
  );
};

export default SubTabbedPanel;
